import React, { useEffect, useRef, useState } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';

import { ErrorResponse, Spinner, SummaryStatCard } from '~components';
import Icon, { faEnvelope } from '~components/icon';

import { BulkSignupFilters, MandateFilter, resolveMandateStates } from '~direct-debits/models';

import { useGetCountQuery } from '~direct-debits/actions/signups';

interface ComponentProps {
  value: {
    mandate_filter?: MandateFilter;
    has_outstanding_invoice?: boolean;
    has_email?: boolean;
    last_sent_days?: number;
  };
  onChange?: (name: string, value: any) => void;
}

const FiltersPreview: React.FC<ComponentProps> = ({ value, onChange }) => {
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedFilters, setDebouncedFilters] = useState<BulkSignupFilters | null>(null);

  const mandateFilter = value?.mandate_filter;

  useEffect(() => {
    if (mandateFilter === undefined) {
      setDebouncedFilters(null);
      return;
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      setDebouncedFilters({
        mandate_states: resolveMandateStates(mandateFilter),
        has_email: value.has_email,
        has_outstanding_invoice: value.has_outstanding_invoice,
        last_sent_days: value.last_sent_days,
      });
    }, 400);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [mandateFilter, value?.has_email, value?.has_outstanding_invoice, value?.last_sent_days]);

  const { data, error, isFetching, isError } = useGetCountQuery(debouncedFilters ?? { mandate_states: [] }, {
    skip: !debouncedFilters,
  });

  // onChange is intentionally omitted from deps below — it's a pass-through to
  // Formik's stable setters, but its identity churns on every parent render,
  // which would turn setField → re-render → new onChange into an infinite loop.
  useEffect(() => {
    if (onChange && data) {
      onChange('preview', { count: data.count ?? 0, filters: debouncedFilters });
    }
  }, [data, debouncedFilters]);

  const count = data?.count ?? 0;

  return (
    <div>
      {isFetching && <Spinner loading={isFetching} />}
      {!isFetching && (
        <SummaryStatCard
          count={<span>{count}</span>}
          title="Clients Matching Filters"
          icon={<Icon icon={faEnvelope} />}
          countClassName="text-primary"
        />
      )}
      {isError && <ErrorResponse message={getErrorMessage(error)} />}
    </div>
  );
};

export default FiltersPreview;
